import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faUndo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContacts } from 'api/contacts/contacts';
import { removeAllPersonalizedLetters } from 'api/engage';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { usePersonalizedLettersStore } from 'assets/js/pages/engage/letters/usePersonalizedLettersStore';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { first } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

const PersonalizedAlertNotification = () => {
    const queryClient = useQueryClient();
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { setCurrentPersonalizedLetterContact, setPersonalizedLettersContactTab } =
        usePersonalizedLettersStore();

    const { isPending, mutate: removePersonalizedLetters } = useMutation({
        mutationFn: () => removeAllPersonalizedLetters(accountId, letter.id),
        onSuccess: () => {
            success('Reverted all personalized letters!');
            queryClient.setQueryData(accountQueryKeys.engageMessageById(accountId, letter.id), {
                ...letter,
                has_personalized_mail_pieces: false,
            });
            prepPersonalizedLetters();
            setPersonalizedLettersContactTab('all');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to remove all personalized letters'),
    });

    const { mutate: prepPersonalizedLetters } = useMutation({
        mutationFn: () =>
            fetchContacts(accountId, {
                page: 1,
                per_page: 1,
                messageId: letter.id,
                channel: 'letter',
                include: 'mailPieces',
                type: ContactType.Individual,
            }),
        onSuccess: data => {
            const contact = first(data.data);
            setCurrentPersonalizedLetterContact(contact);
        },
    });

    if (!letter.has_personalized_mail_pieces) {
        return null;
    }

    return (
        <div className={'mx-4 px-0.5'}>
            <AlertNotification.Root type={'warning'}>
                <AlertNotification.Icon />
                <AlertNotification.Content>
                    <AlertNotification.Title>Personalized letters found</AlertNotification.Title>
                    <AlertNotification.Description>
                        Do you want to revert all personalized mailings to their previous state?
                    </AlertNotification.Description>
                    <Button
                        variant="none"
                        size="sm"
                        color="warning"
                        className="mt-2 font-medium underline"
                        disabled={isPending}
                        onClick={() => removePersonalizedLetters()}
                    >
                        <FontAwesomeIcon icon={faUndo} />
                        Revert
                    </Button>
                </AlertNotification.Content>
            </AlertNotification.Root>
        </div>
    );
};

export { PersonalizedAlertNotification };
